import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography as MuiTypography, Badge as MuiBadge } from '@mui/material';
import { Tag, Modal, Typography, Space, Button } from 'antd';
import { 
  EmailOutlined as EmailIcon, 
  VisibilityOutlined as ViewIcon,
  ReplyOutlined as ReplyIcon,
  DeleteOutlined as DeleteIcon 
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getAllMessages, updateMessageStatus, deleteMessage } from '../services/api';
import DataTable from '../components/common/DataTable';
import PageHeader from '../components/common/PageHeader';
import DeleteConfirmDialog from '../components/common/DeleteConfirmDialog';
import moment from 'moment';

const { Text } = Typography;

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await getAllMessages();
      // Sort by latest first
      const sorted = [...data].sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix());
      setMessages(sorted);
    } catch (error) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setModalVisible(true);
    if (message.status === 'new') {
      handleUpdateStatus(message.id, 'read');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await updateMessageStatus(id, { status });
      fetchMessages();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteMessage(deleteId);
      toast.success('Message deleted successfully');
      fetchMessages();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete message');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'read': return 'green';
      case 'replied': return 'purple';
      default: return 'default';
    }
  };

  const columns = [
    {
      id: 'name',
      label: 'SENDER NAME',
      width: '200px',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MuiTypography sx={{ fontWeight: 600, color: 'var(--header-title)' }}>
            {row.name}
          </MuiTypography>
          {row.status === 'new' && (
            <MuiBadge color="primary" variant="dot" />
          )}
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'EMAIL ADDRESS',
      width: '250px',
      render: (row) => (
        <MuiTypography variant="body2" sx={{ color: 'var(--subtitle-text)' }}>
          {row.email}
        </MuiTypography>
      )
    },
    {
      id: 'subject',
      label: 'SUBJECT',
      render: (row) => (
        <MuiTypography variant="body2" sx={{ fontWeight: 500, color: 'var(--header-title)' }}>
          {row.subject}
        </MuiTypography>
      )
    },
    {
      id: 'status',
      label: 'STATUS',
      width: '120px',
      align: 'center',
      render: (row) => (
        <Tag color={getStatusColor(row.status)}>
          {row.status.toUpperCase()}
        </Tag>
      ),
    },
    {
      id: 'createdAt',
      label: 'RECEIVED ON',
      width: '150px',
      render: (row) => (
        <MuiTypography variant="caption" sx={{ color: 'var(--subtitle-text)', fontWeight: 500 }}>
          {moment(row.createdAt).format('MMM DD, YYYY')}
        </MuiTypography>
      )
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: '180px',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton 
            onClick={() => handleViewMessage(row)}
            size="small"
            title="View Message"
            sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' } }}
          >
            <ViewIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton 
            onClick={() => handleUpdateStatus(row.id, 'replied')}
            size="small"
            title="Mark as Replied"
            disabled={row.status === 'replied'}
            sx={{ color: '#94a3b8', '&:hover': { color: '#9333ea', bgcolor: '#f5f3ff' }, ml: 1 }}
          >
            <ReplyIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton 
            onClick={() => setDeleteId(row.id)}
            size="small"
            title="Delete Message"
            sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' }, ml: 1 }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box className="premium-container">
      <PageHeader
        title="Messages"
        subtitle="Review and respond to inquiries and feedback received through the contact form."
        titleIcon={<EmailIcon />}
      />

      <Box className="premium-table-card">
        <DataTable
          premium={true}
          columns={columns}
          data={messages}
          loading={loading}
          emptyTitle="No Messages Found"
          emptySubtitle="There are no contact inquiries to display at the moment."
        />
      </Box>

      <Modal
        title={`Message from ${selectedMessage?.name}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedMessage && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>From</Text>
              <Text>{selectedMessage.name} ({selectedMessage.email})</Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Phone</Text>
              <Text>{selectedMessage.phone || 'N/A'}</Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Subject</Text>
              <Text>{selectedMessage.subject}</Text>
            </div>
            <Box sx={{ p: 2, bgcolor: 'rgba(248, 250, 252, 1)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Message Body</Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedMessage.message}</Text>
            </Box>
            <div>
              <Text strong style={{ marginRight: 8 }}>Status:</Text>
              <Tag color={getStatusColor(selectedMessage.status)}>
                {selectedMessage.status.toUpperCase()}
              </Tag>
            </div>
          </Space>
        )}
      </Modal>

      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        itemName="message"
        loading={deleteLoading}
      />
    </Box>
  );
};

export default MessagesPage;
